import { ApiClient } from '../core/ApiClient';
import { SaveManager } from '../storage/SaveManager';
import { i18n } from '../core/I18nManager';

export interface LeaderboardEntry {
    username: string;
    avatar_url: string | null;
    best_time_ms: number;
    completed_at: string;
}

export class LeaderboardManager {
    private container: HTMLElement | null = null;
    private puzzleId: string | null = null;
    private difficulty: string = 'easy';

    constructor() {}

    /**
     * 设置挂载容器
     */
    public setContainer(container: HTMLElement) {
        this.container = container;
        this.container.classList.add('leaderboard-widget');
    }

    /**
     * 刷新排行榜数据
     */
    public async refresh(puzzleId: string, difficulty: string) {
        this.puzzleId = puzzleId;
        this.difficulty = difficulty;
        
        if (!this.container) return;

        try {
            const data = await ApiClient.get<LeaderboardEntry[]>(`/api/leaderboard/${puzzleId}?difficulty=${difficulty}&limit=3`);
            this.renderMini(data);
        } catch (error) {
            console.error('Failed to fetch leaderboard', error);
            this.renderError();
        }
    }

    /**
     * 渲染迷你版排行榜 (Top 3)
     */
    private renderMini(entries: LeaderboardEntry[]) {
        if (!this.container) return;

        let html = `
            <div class="leaderboard-header">
                <h3>${i18n.t('leaderboard_title') || 'Leaderboard'}</h3>
            </div>
            <div class="leaderboard-list">
        `;

        if (entries.length === 0) {
            html += `<div class="no-data" style="font-size: 12px; color: var(--zen-ink-light); text-align: center; padding: 10px 0;">${i18n.t('no_records') || 'No records yet'}</div>`;
        } else {
            entries.forEach((entry, index) => {
                const medalClass = index < 3 ? `medal-${index + 1}` : '';
                // 智能头像解析逻辑
                const avatarSrc = entry.avatar_url 
                    ? (entry.avatar_url.startsWith('http') ? entry.avatar_url : `/${entry.avatar_url}`)
                    : `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(entry.username)}`;

                html += `
                    <div class="leaderboard-item ${medalClass}">
                        <span class="rank">${index + 1}</span>
                        <img src="${avatarSrc}" 
                             class="avatar" 
                             onerror="this.src='https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(entry.username)}'" />
                        <span class="username">${entry.username}</span>
                        <span class="time">${SaveManager.formatTime(entry.best_time_ms)}</span>
                    </div>
                `;
            });
        }

        html += `
            </div>
            <button class="more-btn" id="show-full-leaderboard">${i18n.t('more_btn') || 'MORE'}</button>
        `;

        this.container.innerHTML = html;

        this.container.querySelector('#show-full-leaderboard')?.addEventListener('click', () => {
            this.showFullModal();
        });
    }

    private renderError() {
        if (this.container) {
            this.container.innerHTML = `<div class="error" style="font-size: 12px; color: #ef4444;">Failed to load leaderboard</div>`;
        }
    }

    /**
     * 显示全量排行榜模态框
     */
    public async showFullModal() {
        if (!this.puzzleId) return;

        // 创建模态框背景
        const overlay = document.createElement('div');
        overlay.className = 'modal-overlay';
        overlay.innerHTML = `
            <div class="modal-dialog leaderboard-modal">
                <div class="modal-header" style="display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid var(--zen-border);">
                    <h3 style="margin: 0; font-family: 'Noto Serif SC', serif;">${i18n.t('full_leaderboard') || 'Global Ranking'}</h3>
                    <button class="modal-close" style="background: none; border: none; font-size: 24px; cursor: pointer; color: var(--zen-ink-mid);">&times;</button>
                </div>
                <div class="modal-body" style="max-height: 60vh; overflow-y: auto; padding: 0 24px 24px;">
                    <div class="loading-spinner" style="text-align: center; padding: 40px;">Loading...</div>
                </div>
            </div>
        `;

        document.body.appendChild(overlay);

        const closeBtn = overlay.querySelector('.modal-close');
        closeBtn?.addEventListener('click', () => {
            document.body.removeChild(overlay);
        });

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) document.body.removeChild(overlay);
        });

        try {
            const entries = await ApiClient.get<LeaderboardEntry[]>(`/api/leaderboard/${this.puzzleId}?difficulty=${this.difficulty}&limit=50`);
            const body = overlay.querySelector('.modal-body')!;
            
            if (entries.length === 0) {
                body.innerHTML = `<div style="text-align: center; padding: 40px; color: var(--zen-ink-light);">${i18n.t('no_records') || 'No records yet'}</div>`;
                return;
            }

            let html = `
                <table class="leaderboard-table">
                    <thead>
                        <tr>
                            <th>${i18n.t('rank') || 'Rank'}</th>
                            <th>${i18n.t('player') || 'Player'}</th>
                            <th>${i18n.t('time') || 'Time'}</th>
                            <th class="hide-mobile">${i18n.t('date') || 'Date'}</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            entries.forEach((entry, index) => {
                const date = new Date(entry.completed_at).toLocaleDateString();
                const avatarSrc = entry.avatar_url 
                    ? (entry.avatar_url.startsWith('http') ? entry.avatar_url : `/${entry.avatar_url}`)
                    : `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(entry.username)}`;

                html += `
                    <tr>
                        <td class="rank-cell"><span class="rank-badge rank-${index + 1}">${index + 1}</span></td>
                        <td class="player-cell">
                            <img src="${avatarSrc}" 
                                 class="avatar-small" 
                                 onerror="this.src='https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(entry.username)}'" />
                            <span class="username">${entry.username}</span>
                        </td>
                        <td class="time-cell" style="font-family: monospace; font-weight: 500;">${SaveManager.formatTime(entry.best_time_ms)}</td>
                        <td class="date-cell hide-mobile" style="color: var(--zen-ink-light); font-size: 13px;">${date}</td>
                    </tr>
                `;
            });

            html += `
                    </tbody>
                </table>
            `;
            body.innerHTML = html;
        } catch (error) {
            overlay.querySelector('.modal-body')!.innerHTML = `<div class="error" style="text-align: center; padding: 40px; color: #ef4444;">Failed to load records</div>`;
        }
    }
}
